import React from 'react';

import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  CurrencyExchange as CurrencyExchangeIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { Box, IconButton, PaginationItem, Typography } from '@mui/material';
import { DateTime } from 'luxon';

import { DateFormatPatterns, StripePaymentHistoryDetails } from '@clearverify/constants';
import { formatMoney } from '@clearverify/utils';

import type { Maybe } from 'src/api';
import {
  StripeBillingTransactionInfoFragment,
  StripeBillingTransactionStatus,
  useStripeRefundChargeMutation,
} from 'src/api';
import { PaymentHistoryItemChip } from 'src/components/common';
import type { TableColumn } from 'src/components/table';
import { Table, TableRowAction, TableThemes } from 'src/components/table';
import { ModalId } from 'src/constants/modals';
import { useNotification } from 'src/hooks/app';
import { useModalV2 } from 'src/hooks/shared';
import { useStripePaymentHistory } from 'src/hooks/stripe';
import { useCurrentUserPermissions } from 'src/hooks/user';
import { useCurrentUserVerifier } from 'src/hooks/verifier';
import { EntityActionConfirmationModal } from 'src/modals/common';

interface PaymentHistoryTableArgs {
  verifierId?: Maybe<string>;
  useCurrentUser?: boolean;
  showVerifier?: boolean;
  showRefundAction?: boolean;
}

const getHistoryItemCreationDate = (date: string) => {
  return DateTime.fromISO(date).toFormat(DateFormatPatterns.shortDateWithSlash);
};

export const PaymentHistoryTable: React.FC<PaymentHistoryTableArgs> = ({
  verifierId,
  useCurrentUser = true,
  showVerifier,
  showRefundAction,
}) => {
  const { openModal } = useModalV2();
  const { createApolloContext } = useNotification();

  const { hasVerifierRoles } = useCurrentUserPermissions();
  const { verifier: currentVerifier } = useCurrentUserVerifier();

  const [refundCharge] = useStripeRefundChargeMutation({
    context: createApolloContext({
      successMessage: 'The refund has been processed',
      errorMessage: 'Failed to process the refund',
    }),
  });

  const verifierResolvedId = (useCurrentUser ? currentVerifier?.id : verifierId) || undefined;

  const {
    revenueHistoryData,
    loadingRevenueHistory,
    hasNextPage,
    pageNumber,
    canGoBack,
    canGoNext,
    goBack,
    goNext,
    refetch,
  } = useStripePaymentHistory({
    verifierId: verifierResolvedId,
    skip: hasVerifierRoles && !verifierResolvedId,
  });

  const rowActions = React.useMemo(
    (): Array<TableRowAction<StripeBillingTransactionInfoFragment>> => [
      {
        startIcon: <ReceiptIcon style={{ margin: '0 10px 0 0' }} />,
        actionName: 'View Receipt',
        onAction: paymentHistoryDataItem => {
          if (!paymentHistoryDataItem.receiptUrl) {
            return;
          }

          window.open(paymentHistoryDataItem.receiptUrl);
        },
        disabled: paymentHistoryDataItem => !paymentHistoryDataItem.receiptUrl,
      },
      {
        startIcon: <CurrencyExchangeIcon style={{ margin: '0 10px 0 0' }} />,
        actionName: 'Refund',
        onAction: paymentHistoryDataItem => {
          openModal(EntityActionConfirmationModal, {
            id: ModalId.EntityActionConfirmation,
            onSubmit: async () => {
              await refundCharge({ variables: { stripeChargeId: paymentHistoryDataItem.id } });
              await refetch();
            },
            action: 'refund',
            entity: 'transaction',
            actionButtonColor: 'error',
          });
        },
        disabled: paymentHistoryDataItem =>
          paymentHistoryDataItem.status !== StripeBillingTransactionStatus.Succeeded ||
          paymentHistoryDataItem.isRefunded,
        hidden: !showRefundAction,
      },
    ],
    [openModal, refetch, refundCharge, showRefundAction],
  );

  const columns = React.useMemo(
    (): Array<TableColumn<StripeBillingTransactionInfoFragment>> => [
      {
        title: 'Transaction ID',
        key: 'id',
        render: (_valueByDataPath, paymentHistoryDataItem) => {
          return <Typography variant="body2">{paymentHistoryDataItem.id}</Typography>;
        },
      },
      {
        title: 'Date',
        key: 'createdAt',
        render: (_valueByDataPath, paymentHistoryDataItem) => {
          return (
            <Typography variant="body2">
              {getHistoryItemCreationDate(paymentHistoryDataItem.createdAt)}
            </Typography>
          );
        },
      },
      {
        title: 'Verifier',
        key: 'verifierDisplayName',
        hidden: !showVerifier,
        render: (_valueByDataPath, paymentHistoryDataItem) => {
          const { verifierEmail, verifierDisplayName } = paymentHistoryDataItem;
          if (!verifierEmail) {
            return <Typography variant="body2">—</Typography>;
          }

          return (
            <Typography variant="body2">
              {verifierEmail}
              {verifierDisplayName ? ` (${verifierDisplayName})` : ''}
            </Typography>
          );
        },
      },
      {
        title: 'Detail',
        key: 'description',
        render: (_valueByDataPath, paymentHistoryDataItem) => {
          let content = '—';
          if (isStripePaymentHistoryDetails(paymentHistoryDataItem.description)) {
            content = StripePaymentHistoryDetails[paymentHistoryDataItem.description];
          }
          return <Typography variant="body2">{content}</Typography>;
        },
      },
      {
        title: 'Payment method',
        key: 'paymentMethod',
        render: (_valueByDataPath, paymentHistoryDataItem) => {
          return (
            <Typography variant="body2">
              {paymentHistoryDataItem.cardBrand
                ? `${paymentHistoryDataItem.cardBrand.toUpperCase()} — ${
                    paymentHistoryDataItem.cardLast4
                  }`
                : '—'}
            </Typography>
          );
        },
      },
      {
        title: 'Amount',
        key: 'amount',
        render: (_valueByDataPath, paymentHistoryDataItem) => {
          return (
            <Typography variant="body2">{formatMoney(paymentHistoryDataItem.amount, 0)}</Typography>
          );
        },
      },
      {
        title: 'Status',
        key: 'status',
        render: (_valueByDataPath, paymentHistoryDataItem) => {
          return (
            <PaymentHistoryItemChip
              status={paymentHistoryDataItem.status}
              isRefunded={paymentHistoryDataItem.isRefunded}
            />
          );
        },
      },
    ],
    [showVerifier],
  );

  return (
    <React.Fragment>
      <Table
        dataSource={revenueHistoryData}
        rowCount={-1}
        columns={columns}
        rowActions={rowActions}
        loading={loadingRevenueHistory}
        paginationHasNextPage={hasNextPage}
        hideTableTopMenu
        hidePagination
        hideFooter
        tableTheme={TableThemes.withBorderWhole}
      />

      <Box display="flex" justifyContent="flex-end" alignItems="center" mt={2}>
        <IconButton size="small" onClick={goBack} disabled={!canGoBack || loadingRevenueHistory}>
          <ChevronLeftIcon />
        </IconButton>
        <PaginationItem page={pageNumber} selected />
        <IconButton size="small" onClick={goNext} disabled={!canGoNext || loadingRevenueHistory}>
          <ChevronRightIcon />
        </IconButton>
      </Box>
    </React.Fragment>
  );
};

const isStripePaymentHistoryDetails = (
  description: unknown,
): description is keyof typeof StripePaymentHistoryDetails => {
  return typeof description === 'string' && description in StripePaymentHistoryDetails;
};
